import { z } from 'zod';

/**
 * Dynamic time-series sample where each rower position is a numeric column.
 * Keys beyond `time` (e.g., "Rower 1") are allowed and validated as numbers or null.
 */
export const PowerTimeSeriesSampleSchema = z
  .object({
    time: z.number(),
  })
  .catchall(z.number().nullable());

export type PowerTimeSeriesSample = z.infer<typeof PowerTimeSeriesSampleSchema>;

export const StrokeBoundarySchema = z
  .object({
    start: z.number(),
    end: z.number().nullable().optional(),
  })
  .strict();

const PowerTimeSeriesContractInternalSchema = z.object({
  contract: z.literal('PowerTimeSeries'),
  version: z.literal('1.0.0'),
  sessionId: z.string(),
  samples: z.array(PowerTimeSeriesSampleSchema),
  meta: z
    .object({
      source: z.enum(['sampled_rpc', 'fallback']).optional(),
      strokeBoundaries: z.array(StrokeBoundarySchema).optional(),
      length: z.number().int().nonnegative().optional(),
    })
    .partial()
    .optional(),
});

export type PowerTimeSeriesContract = z.infer<typeof PowerTimeSeriesContractInternalSchema>;

type BuildContractArgs = {
  sessionId: string;
  samples: unknown;
  meta?: PowerTimeSeriesContract['meta'];
};

/**
 * Creates a validated PowerTimeSeries contract payload.
 * Automatically injects contract id/version and derived meta information.
 */
export const buildPowerTimeSeriesContract = ({
  sessionId,
  samples,
  meta,
}: BuildContractArgs): PowerTimeSeriesContract => {
  const payload = {
    contract: 'PowerTimeSeries' as const,
    version: '1.0.0' as const,
    sessionId,
    samples,
    meta: {
      length: Array.isArray(samples) ? samples.length : 0,
      ...meta,
    },
  };

  return PowerTimeSeriesContractInternalSchema.parse(payload);
};

/**
 * Lightweight validator for callers that already assemble the contract payload.
 */
export const validatePowerTimeSeriesContract = (contract: unknown) =>
  PowerTimeSeriesContractInternalSchema.parse(contract);
